import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { useQueryClient } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import { useUnits } from "@/hooks/useUnits"
import { UnitCombobox } from "@/components/ui/unit-combobox"

interface EditarMoradorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  morador: any
}

interface MoradorFormData {
  full_name: string
  email: string
  phone: string
  apartment_number: string
  building: string
  role: string
  resident_type: string
  unit_id: string
  new_password?: string
}

export default function EditarMoradorModal({ open, onOpenChange, morador }: EditarMoradorModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [changePassword, setChangePassword] = useState(false)
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { data: units } = useUnits()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm<MoradorFormData>()

  const selectedRole = watch("role")
  const selectedResidentType = watch("resident_type")
  const selectedUnitId = watch("unit_id")

  useEffect(() => {
    if (morador && open) {
      setValue("full_name", morador.full_name || "")
      setValue("email", morador.email || "")
      setValue("phone", morador.phone || "")
      setValue("apartment_number", morador.apartment_number || "")
      setValue("building", morador.building || "")
      setValue("role", morador.role || "morador")
      setValue("resident_type", morador.resident_type || "owner")
      setValue("unit_id", morador.unit_id || "")
      setChangePassword(false)
    }
  }, [morador, open, setValue])

  const onSubmit = async (data: MoradorFormData) => {
    setIsLoading(true)
    try {
      // Atualizar perfil
      const profileData: any = {
        full_name: data.full_name,
        email: data.email,
        phone: data.phone,
        apartment_number: data.apartment_number,
        building: data.building,
        role: data.role,
        resident_type: data.resident_type,
        unit_id: data.unit_id || null,
        updated_at: new Date().toISOString()
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update(profileData)
        .eq('id', morador.id)

      if (profileError) throw profileError

      // Trocar senha se solicitado
      if (changePassword && data.new_password) {
        try {
          const response = await fetch('https://umnoiivuntnvsxkysvrr.supabase.co/functions/v1/change-user-password', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
            },
            body: JSON.stringify({
              userId: morador.id,
              newPassword: data.new_password
            })
          })

          const result = await response.json()
          
          if (!response.ok) {
            throw new Error(result.error || 'Erro ao alterar senha')
          }
        } catch (passwordError: any) {
          console.error('Erro ao alterar senha:', passwordError)
          toast({
            title: "Aviso",
            description: "Morador atualizado, mas não foi possível alterar a senha: " + passwordError.message,
            variant: "destructive",
          })
        }
      }

      toast({
        title: "Sucesso!",
        description: `${data.full_name} foi atualizado com sucesso.`,
      })

      queryClient.invalidateQueries({ queryKey: ["profiles"] })
      onOpenChange(false)
      reset()
    } catch (error: any) {
      console.error('Erro ao atualizar morador:', error)
      toast({
        title: "Erro",
        description: "Erro ao atualizar morador: " + error.message,
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    onOpenChange(false)
    reset()
    setChangePassword(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Editar Morador</DialogTitle>
          <DialogDescription>
            Edite as informações do morador. Como síndico, você pode alterar todos os dados e a senha.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4">
            <div>
              <Label htmlFor="full_name">Nome Completo *</Label>
              <Input
                id="full_name"
                {...register("full_name", { required: "Nome é obrigatório" })}
                placeholder="Digite o nome completo"
              />
              {errors.full_name && (
                <p className="text-sm text-red-500">{errors.full_name.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                {...register("email", { 
                  required: "Email é obrigatório",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Email inválido"
                  }
                })}
                placeholder="Digite o email"
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="phone">Telefone</Label>
              <Input
                id="phone"
                {...register("phone")}
                placeholder="(11) 99999-9999"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="building">Bloco</Label>
                <Input
                  id="building"
                  {...register("building")}
                  placeholder="A"
                />
              </div>
              <div>
                <Label htmlFor="apartment_number">Apartamento</Label>
                <Input
                  id="apartment_number"
                  {...register("apartment_number")}
                  placeholder="101"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="resident_type">Tipo de Residente *</Label>
              <Select value={selectedResidentType} onValueChange={(value) => setValue("resident_type", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Proprietário</SelectItem>
                  <SelectItem value="tenant">Locatário</SelectItem>
                  <SelectItem value="both">Proprietário e Locatário</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="unit_id">Unidade</Label>
              <UnitCombobox
                units={units || []}
                value={selectedUnitId}
                onChange={(value) => setValue("unit_id", value)}
                placeholder="Selecionar unidade"
              />
            </div>

            <div>
              <Label htmlFor="role">Tipo de Usuário *</Label>
              <Select value={selectedRole} onValueChange={(value) => setValue("role", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo de usuário" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="morador">Morador</SelectItem>
                  <SelectItem value="porteiro">Porteiro</SelectItem>
                  <SelectItem value="sindico">Síndico</SelectItem>
                  <SelectItem value="administracao">Administração</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="changePassword"
                  checked={changePassword}
                  onChange={(e) => setChangePassword(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="changePassword">Alterar senha</Label>
              </div>
              
              {changePassword && (
                <div>
                  <Label htmlFor="new_password">Nova Senha</Label>
                  <Input
                    id="new_password"
                    type="password"
                    {...register("new_password", {
                      required: changePassword ? "Nova senha é obrigatória" : false,
                      minLength: changePassword ? {
                        value: 6,
                        message: "Senha deve ter pelo menos 6 caracteres"
                      } : undefined
                    })}
                    placeholder="Digite a nova senha"
                  />
                  {errors.new_password && (
                    <p className="text-sm text-red-500">{errors.new_password.message}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar Alterações
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}